import { Inject, Injectable, Optional } from '@angular/core';
import {
    InAppFeedBackInfo,
    initializeInAppFeedback,
    updateFeedbackOptions
} from '@office-start/feedback-sdk-service';
import { HARMONY_APP_NAME, IFeedbackInitOptions } from '@office-start/feedback-sdk-types';
import { logError, logPerformance, logUserAction } from '@office-start/telemetry-service';
import { KnownPerfInstrumentationEvents } from '@office-start/telemetry-types';
// Remove from shellsuite.d.ts after import bug is fixed.
// import { FeedbackMetadata } from '@1js/suiteux-shared/suiteux-interfaces';
import { WindowService } from '../../shared/services/window.service';
import { OfficeRoute } from '../constants/office-route.enum';
import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownInstrumentationEvents } from '../instrumentation/instrumentation.service';
import { FileType } from '../models/file-item.model';
import { WrapperVersion } from '../models/wrapper-version.model';
import { CapabilitiesService } from '../shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from '../shared-context/client-startup-config.service';
import { ContextService } from '../shared-context/context.service';
import { UserContextService } from '../shared-context/user-context.service';
import { AdminPolicy, AdminPolicyService } from './admin-policy.service';
import { DeviceDetector } from './device-detector.service';
import { DocumentService } from './document.service';
import { FeedbackConfigService } from './feedback-config.service';
import { defaultFeedbackOptions } from './harmonyFeedbackOptions';
import { OfficeRouteItem, OfficeRouteService } from './office-route.service';
import { Performance } from './performance.service';
import { SchedulePriority, SchedulerService } from './scheduler.service';
import { ShellShimService } from './shell-shim.service';
import { WRAPPER_VERSION } from './wrapper-version.token';

const enum FeedbackUserCategory {
    Admin = 'Admin',
    Commercial = 'Commercial',
    Consumer = 'Consumer',
    Dynamics365 = 'Dynamics365',
    Edu = 'Edu',
    EduFaculty = 'EduFaculty',
    EduStudent = 'EduStudent',
    FirstRelease = 'FirstRelease',
    Smb = 'Smb'
}

const WorldWide = 'WorldWide';
const Production = 'Production';

@Injectable({
    providedIn: 'root'
})
export class FeedbackService {
    private readonly feedbackScript = 'feedback-script';
    private currentPageView: string;
    private scriptLoadStarted = false;

    public readonly feedbackServiceInitializePromise: Promise<void> = null ;
    public OCVAdminPolicy: AdminPolicy;

    constructor(
        private readonly capabilitiesService: CapabilitiesService,
        private readonly clientStartupConfigService: ClientStartupConfigService,
        private readonly deviceDetector: DeviceDetector,
        private readonly documentService: DocumentService,
        private readonly feedbackConfigService: FeedbackConfigService,
        private readonly instrumentationService: InstrumentationService,
        private readonly routeService: OfficeRouteService,
        private readonly startupConfigService: ClientStartupConfigService,
        private readonly userContextService: UserContextService,
        private readonly scheduler: SchedulerService,
        private readonly windowService: WindowService,
        private readonly shellShimService: ShellShimService,
        private readonly contextService: ContextService,
        private readonly adminPolicyService: AdminPolicyService,
        @Optional() @Inject(WRAPPER_VERSION) private readonly wrapperVersion: WrapperVersion
    ) {
        this.feedbackServiceInitializePromise = this.initialize();
    }

    public getOCVTelemetryData(scriptInsertionDuration: string) {
        const telemetryGroup = this.getTelemetryGroup();
        const launchOptions = {
            applicationGroup: {
                appData: JSON.stringify({
                    'userCategories': this.getUserCategories(),
                    'latency': scriptInsertionDuration
                })
            },
            telemetryGroup,
            webGroup: {
                browser: this.deviceDetector.getDeviceInfo().browser.keyword,
                sourcePageName: this.getSourcePageName()
            }
        };
        return launchOptions;
    }

    public loadOCVFeedback(onDismiss?: () => void, isDarkMode?: boolean): void {
        if (this.capabilitiesService.capabilities.firstDraftFeedbackEnabled) {
            const ocvLaunchOptions = this.getOCVTelemetryData('0');
            const policy = this.OCVAdminPolicy;
            const FeedbackInitOptions: IFeedbackInitOptions = {};
            const defaultFeedback = defaultFeedbackOptions({ ocvLaunchOptions, policy, FeedbackInitOptions, darkMode: isDarkMode });
            const hostAppName = HARMONY_APP_NAME;
            const appLanguage = this.contextService.sharedContext.officeMarket ?? 'en-US';

            const feedbackInfo: InAppFeedBackInfo = {
                hostName: hostAppName,
                language: appLanguage
            };

            initializeInAppFeedback(feedbackInfo);
            updateFeedbackOptions(defaultFeedback);
        } else {
            if (typeof OfficeBrowserFeedback === 'undefined') {
                return;
            }

            OfficeBrowserFeedback.initOptions.appId = this.getOcvAppId();

            OfficeBrowserFeedback.initOptions.onDismiss = (isSubmitted: boolean) => {
                this.logDismissOCVFeedback(isSubmitted);
                if (onDismiss) {
                    onDismiss();
                }
            };

            const scriptHasLoaded = !!OfficeBrowserFeedback.multiFeedback;

            // Load the script only when it hasn't been loaded yet
            if (!scriptHasLoaded && !this.scriptLoadStarted) {
                const onSuccess = (scriptInsertionDuration: number) => {
                    this.displayOCVFeedback(scriptInsertionDuration);
                };
                this.addOCVScriptToPage(onSuccess);
            } else {
                this.displayOCVFeedback(0);
            }
        }
    }

    public addOCVScriptToPage(onSuccess?: (insertionDuration: number) => void): boolean {
        const config = this.feedbackConfigService.config;

        if (!config) {
            return false;
        }

        if (this.scriptLoadStarted) {
            return true;
        }

        this.scriptLoadStarted = true;

        this.scheduler.scheduleWork(
            () => {
                const scriptInsertStart = Performance.now();
                this.documentService.addScriptToPage(config.scriptUrl, () => {
                    const scriptInsertDuration = Math.ceil(Performance.now() - scriptInsertStart);
                    onSuccess(scriptInsertDuration);
                }, () => { /* Do nothing */ }, true, this.feedbackScript, true);
            },
            SchedulePriority.VeryLow
        );

        return true;
    }

    public getOcvAppId(): number {
        const currentPage = this.getSourcePageName();

        if (currentPage === this.feedbackConfigService.config.fluidContentPage) {
            return this.feedbackConfigService.config.fluidAppId;
        }

        return this.feedbackConfigService.config.appId;
    }

    private async initialize() {
        this.currentPageView = this.getCurrentPageView(this.routeService.getCurrentRoute());
        this.routeService.subscribeToRouteChangeStart(event => this.currentPageView = this.getCurrentPageView(event.currentRoute));

        if (this.capabilitiesService.capabilities.adminPolicyEnabled) {
            this.OCVAdminPolicy = await this.adminPolicyService.getAdminPolicy();
        }

        if (typeof OfficeBrowserFeedback !== 'undefined' && !OfficeBrowserFeedback.initOptions) {
            const { stylesUrl, intlUrl, environment, screenshotEnabled, autopopulateEmail } = this.feedbackConfigService.config;
            const { buildId, correlationId } = this.startupConfigService.config.sharedClientStartupContext;

            OfficeBrowserFeedback.initOptions = {
                appId: undefined,
                stylesUrl,
                intlUrl,
                environment,
                sessionId: correlationId,
                build: buildId,
                locale: this.contextService.sharedContext.officeMarket,
                screenshot: screenshotEnabled,
                userEmail: autopopulateEmail ? this.startupConfigService.config.userPrincipalName : ''
            };

            if (this.OCVAdminPolicy) {
                OfficeBrowserFeedback.initOptions.screenshot = this.OCVAdminPolicy.ScreenShot.State;
                OfficeBrowserFeedback.initOptions.showEmailAddress = this.OCVAdminPolicy.EmailCollection.State;
            }
        }

        if (this.capabilitiesService.capabilities.shellFeedbackEnabled) {
            const launchOptions = this.getOCVTelemetryData('0');
            const self = this;

            const shellShim = await this.shellShimService.getShellShim();

            shellShim.NFD.OnFeedbackMetadataRequested((feedbackMetadata: FeedbackMetadata, provideScreenshot: (base64Screenshot: string) => void) => {
                self.windowService.nativeWindow.OfficeBrowserFeedback.html2canvas(this.documentService.nativeDocument.body, { background: '#ffffff' }).then(
                    function (canvas: any) {
                        provideScreenshot(canvas.toDataURL('image/jpeg'));
                    }
                );

                feedbackMetadata = feedbackMetadata || { options: { telemetryGroup: {}, webGroup: {} } } as any; // TODO: remove `as any` cast, should this conform to FeedbackMetadata interface?
                const options = feedbackMetadata.options;

                options.appId = this.getOcvAppId();
                options.userVoiceEnabled = false;
                options.applicationGroup.appData = launchOptions.applicationGroup.appData;
                options.disableIncludeScreenshot = false;

                if (this.OCVAdminPolicy) {
                    options.emailPolicyValue = this.OCVAdminPolicy.EmailCollection.PolicyValue;
                    options.screenshotPolicyValue = this.OCVAdminPolicy.ScreenShot.PolicyValue;
                    options.sendFeedbackPolicyValue = this.OCVAdminPolicy.SendFeedback.PolicyValue;
                    options.logCollectionPolicyValue = this.OCVAdminPolicy.LogCollection.PolicyValue;
                }

                const telemetryGroup = options.telemetryGroup;
                telemetryGroup.audienceGroup = launchOptions.telemetryGroup.audienceGroup;
                telemetryGroup.featureArea = this.getSourcePageName();
                telemetryGroup.flights = launchOptions.telemetryGroup.flights;
                telemetryGroup.processSessionId = self.startupConfigService.config.sharedClientStartupContext.correlationId;
                telemetryGroup['officeBuild'] = launchOptions.telemetryGroup.officeBuild;

                const webGroup = options.webGroup as any;
                webGroup['sourcePageName'] = this.getSourcePageName();

                return feedbackMetadata;
            });
        }
    }

    private getTelemetryGroup(): any {
        const tenantId = this.startupConfigService.config.authType === AuthType.MSA ?
            this.startupConfigService.config.fixedConsumerTenantId :
            this.startupConfigService.config.tenantId;
        const versionPrefix = this.wrapperVersion ? `${this.wrapperVersion.major}.${this.wrapperVersion.minor}.${this.wrapperVersion.build}.${this.wrapperVersion.revision}|` : '';
        const telemetryGroupWeb = {
            audienceGroup: this.getAudienceGroup(),
            deviceType: this.getDeviceType(),
            flights: this.startupConfigService.config.sharedClientStartupContext.flights,
            loggableUserId: this.startupConfigService.config.puid,
            officeBuild: versionPrefix + this.startupConfigService.config.sharedClientStartupContext.buildId,
            platform: this.deviceDetector.getDeviceInfo().platform.keyword,
            tenantId
        };
        //  Desktop NPS survey requires a channel value of 'CC' to onboard
        const telemetryGroupDesktop = {
            ...telemetryGroupWeb,
            channel: 'CC',
            osBuild: 'Unknown'
        };
        if (typeof Windows !== 'undefined' &&
            typeof (Windows.System.Profile.SystemManufacturers) !== 'undefined' &&
            typeof (Windows.System.Profile.SystemManufacturers.SystemSupportInfo) !== 'undefined' &&
            Windows.System.Profile.SystemManufacturers.SystemSupportInfo.localDeviceInfo?.operatingSystem?.version) {
                const version = Windows.System.Profile.SystemManufacturers.SystemSupportInfo.localDeviceInfo.operatingSystem.version;
                telemetryGroupDesktop.osBuild = version.major.toString() + '.' + version.minor.toString() + '.' + version.build.toString();
        }

        return (this.capabilitiesService.capabilities.isPwaInWebView2 || this.capabilitiesService.capabilities.isPwaInUwp) ? telemetryGroupDesktop : telemetryGroupWeb;
    }

    private logDismissOCVFeedback(isSubmitted: boolean): void {
        // Instrument OCV feedback submitted/dismissed
        const eventName = isSubmitted ? KnownInstrumentationEvents.FeedbackOCVSubmitted : KnownInstrumentationEvents.FeedbackOCVDismissed;
        const params: InstrumentationParams = {};
        this.instrumentationService.logGenericEvent(eventName, params);
        logUserAction({
            id: 'feedback-dismiss',
            area: 'Feedback',
            result: isSubmitted ? 'New' : 'Cancel',
            target: 'Feedback'
        });
    }

    private async displayOCVFeedback(scriptInsertionDuration: number): Promise<void> {
            const params: InstrumentationParams = {};
            const durationString = scriptInsertionDuration.toString();
            params[InstrumentationParam.Latency] = durationString;

            if (this.capabilitiesService.capabilities.shellFeedbackEnabled) {
                const shellShim = await this.shellShimService.getShellShim();
                shellShim.NFD.OpenFeedback();
            } else {
                const launchOptions = this.getOCVTelemetryData(durationString);
                // Not sure why this cast is necessary. Bug in OCV typings?
                (OfficeBrowserFeedback.multiFeedback(launchOptions) as unknown as Promise<void>).then(
                    () => { /* succeeded */ },
                    err => {
                        params[InstrumentationParam.Error] = err;
                        // Instrument OCV feedback error
                        this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.FeedbackOCVError, params);
                        logError({
                            eventName: 'Feedback_OCV',
                            errorType: 'Feedback_Error',
                            message: err
                        });
                    });
            }

                // Instrument OCV feedback click
                this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.FeedbackOCVClick, params);
                logUserAction({
                    id: 'feedback-button',
                    area: 'Feedback',
                    result: 'Open',
                    target: 'Feedback'
                });
                logPerformance({
                    eventName: KnownPerfInstrumentationEvents.OCVScriptDownloaded,
                    latency: scriptInsertionDuration,
                    area: 'Feedback'
                });
        }

    private getDeviceType(): string {
        // Laptop is used here for all other values (there are no other permitted type names)
        return this.deviceDetector.isDesktopDevice() ? 'Desktop'
            : this.deviceDetector.isTabletDevice() ? 'Tablet'
                : this.deviceDetector.isMobileDevice() ? 'Phone'
                    : 'Laptop';
    }

    private getAudienceGroup(): string {
        // WorlWide (for GA Commercial and Consumer users) will be mapped to "Production" and rest of the sub-rings of office.com will remain unchanged (ex: Microsoft, Harmony, TargetedRelease and TaosDogfood)
        return this.clientStartupConfigService.config.sharedClientStartupContext.ecsRing === WorldWide ? Production : this.clientStartupConfigService.config.sharedClientStartupContext.ecsRing;
    }

    private getSourcePageName(): string {
        return this.currentPageView;
    }

    /*
     * Returns a list of categories the user belongs to.
     */
    private getUserCategories(): Array<string> {
        const userType: Array<string> = [];
        if (this.startupConfigService.config.authType === AuthType.OrgId) {
            userType.push(FeedbackUserCategory.Commercial);
        } else if (this.startupConfigService.config.authType === AuthType.MSA) {
            userType.push(FeedbackUserCategory.Consumer);
        }

        if (this.userContextService.user.isAdmin) {
            userType.push(FeedbackUserCategory.Admin);
        }

        if (this.userContextService.user.isEdu) {
            userType.push(FeedbackUserCategory.Edu);
        }

        if (this.userContextService.user.isEduStudent) {
            userType.push(FeedbackUserCategory.EduStudent);
        }

        if (this.userContextService.user.isEduFaculty) {
            userType.push(FeedbackUserCategory.EduFaculty);
        }

        if (this.userContextService.user.isSmb) {
            userType.push(FeedbackUserCategory.Smb);
        }

        if (this.userContextService.user.isDynamics) {
            userType.push(FeedbackUserCategory.Dynamics365);
        }

        if (this.userContextService.user.isFirstRelease) {
            userType.push(FeedbackUserCategory.FirstRelease);
        }

        return userType;
    }

    private getCurrentPageView(currentRoute: OfficeRouteItem): string {
        if (!currentRoute || !currentRoute.route) {
            return 'Unknown';
        }

        switch (currentRoute.route) {
            // Using legacy instrumentation events for OfficeHome, AppGallery, and Search to match older feedback in OCV
            case OfficeRoute.OfficeHome:
                return KnownInstrumentationEvents.HomePageView;
            case OfficeRoute.AppGallery:
                return KnownInstrumentationEvents.AppGalleryPageView;
            case OfficeRoute.Search:
            case OfficeRoute.SearchSiteScoped:
            case OfficeRoute.SearchTopicScoped:
            case OfficeRoute.SearchMeetingScoped:
            case OfficeRoute.SearchPersonScoped:
            case OfficeRoute.SearchPersonScopedOverview:
                return KnownInstrumentationEvents.SearchPageView;
            default:
                return `${currentRoute.app ? FileType[currentRoute.app] : ''}${currentRoute.route}`;
        }
    }
}
